import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from '../../utils/toast';
import 'react-toastify/dist/ReactToastify.css';

import AdminNav from '../../components/admin/AdminNav';
import JoinRequestsPanel from '../../components/admin/JoinRequestsPanel';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SupplierInfoModal from '../../components/common/SupplierInfoModal';

import api from '../../utils/api';
import { getCurrentUser } from '../../utils/auth';
import { stateLabel } from '../../utils/usStates';
import { useTranslation } from 'react-i18next';
import { translateKnown } from '../../i18n/knownMessages';
import TruncatedText from '../../components/common/TruncatedText';
import GroupRolesPanel from '../../components/admin/GroupRolesPanel';
import Pagination from '../../components/common/Pagination';
import SupplierImages from '../../components/common/SupplierImages';
import { animalLabel } from '../../utils/animals';
import { useIsMobile } from '../../utils/useIsMobile';

const EPSILON = 0.001;
const GROUPS_PER_PAGE = 10;

// Effective status is computed from remaining cost; a stored "full" is overridden when cost remains
const effectiveStatusOf = (group) => {
  const rem = Number(group.remainingCost ?? group.totalCost ?? 0);
  if (group.status === 'closed') return 'closed';
  if (rem <= EPSILON) return 'full';
  return 'open';
};

const sortValueOf = (group, key) => {
  if (key === 'effectiveStatus') return effectiveStatusOf(group);
  if (key === 'visibility') return group.visibility || 'public';
  if (key === 'supplierId') return group.supplierId?.name || '';
  if (key === 'createdBy') return group.createdBy?.username || '';
  if (key === 'createdAt' || key === 'updatedAt') return group[key] ? new Date(group[key]).getTime() : 0;
  return (group[key] ?? '').toString().toLowerCase();
};

export default function AdminGroupsPage() {
  const { t } = useTranslation();
  // Read by the fetch callback, which must not re-run (and refetch) when the language changes
  const tRef = useRef(t);
  tRef.current = t;
  /** ===============================
   *  STATE
   * =============================== */
  const [groups, setGroups] = useState([]);
  const groupsRequestSeq = useRef(0);
  // Email links open /admin/groups?group=<id>&request=<id> to show the request for review
  const [searchParams] = useSearchParams();
  const reviewGroupId = searchParams.get('group');
  const reviewRequestId = searchParams.get('request');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [addMemberInput, setAddMemberInput] = useState({});
  const [openMenu, setOpenMenu] = useState(null); // key of the member whose ⋮ menu is open
  const [expandedGroupId, setExpandedGroupId] = useState(null); // only one group's Members panel open at a time
  // A member's ⋮ menu belongs to the open Members panel: closing it or switching groups clears it
  useEffect(() => { setOpenMenu(null); }, [expandedGroupId]);
    const isMobile = useIsMobile();
    const [expandedCardId, setExpandedCardId] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState({
    isOpen: false,
    groupId: null,
    email: null,
  });

  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null); // contact is authorized per group by the server

  const [searchQuery, setSearchQuery] = useState('');

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [deleteTarget, setDeleteTarget] = useState(null);

  /** ===============================
   *  AUTH + SCOPE
   * =============================== */
  const currentUser = getCurrentUser();
  const role = (currentUser?.role || '').toLowerCase();
  // Comma-joined so effects depend on a string, not a new array each render
  const isSupplierScoped = role === 'supplieradmin' && (currentUser?.supplierIds || []).length > 0;
  const scopedSupplierKey = isSupplierScoped ? currentUser.supplierIds.join(',') : '';

  /** ===============================
   *  API: FETCH GROUPS
   * =============================== */
  const fetchGroups = useCallback(async () => {
    setLoadError(null);
    const seq = ++groupsRequestSeq.current;
    try {
      const res = await api.get('/api/groups');
      // A newer list or photo update happened while this request was in flight: drop the stale response
      if (seq !== groupsRequestSeq.current) return;
      let list = [];

      if (Array.isArray(res.data)) {
        list = res.data;
      } else if (Array.isArray(res.data.groups)) {
        list = res.data.groups;
      }

      if (scopedSupplierKey) {
        const allowed = scopedSupplierKey.split(',');
        list = list.filter((g) => allowed.includes(String(g.supplierId?._id || g.supplierId)));
      }

      setGroups(list);
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setLoadError('Failed to load groups');
      toast.error(tRef.current('adminGroups.fetchFailed'));
    } finally {
      // Only the newest request decides the loading state; a stale one must not end it early
      if (seq === groupsRequestSeq.current) setLoading(false);
    }
  }, [scopedSupplierKey]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Reload when the tab is focused again, so permission changes made elsewhere show up (no polling)
  useEffect(() => {
    window.addEventListener('focus', fetchGroups);
    return () => window.removeEventListener('focus', fetchGroups);
  }, [fetchGroups]);

  /** ===============================
   *  HELPERS
   * =============================== */
  const safeGroups = useMemo(() => (Array.isArray(groups) ? groups : []), [groups]);

  // 🔍 Filter by status + search query
  const filtered = useMemo(() => {
    let list = safeGroups;

    if (statusFilter === 'effective') {
      list = list.filter((g) => {
        const rem = Number(g.remainingCost ?? 0);
        if (g.status === 'closed') return false;
        return rem > EPSILON;
      });
    } else if (statusFilter !== 'all') {
      list = list.filter((g) => g.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((g) => {
        const supplier = g.supplierId?.name?.toLowerCase() || '';
        const groupName = g.name?.toLowerCase() || '';
        const state = g.state?.toLowerCase() || '';
        return supplier.includes(q) || groupName.includes(q) || state.includes(q);
      });
    }

    return list;
  }, [safeGroups, statusFilter, searchQuery]);

  const sortedFiltered = useMemo(() => {
    if (!sortConfig.key) return filtered;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = sortValueOf(a, sortConfig.key);
      const bv = sortValueOf(b, sortConfig.key);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [filtered, sortConfig]);

  const indexOfLast = currentPage * GROUPS_PER_PAGE;
  const indexOfFirst = indexOfLast - GROUPS_PER_PAGE;
  const currentGroups = sortedFiltered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sortedFiltered.length / GROUPS_PER_PAGE);
  // Filtering can shrink the page count below the current page; move back to the last valid page
  useEffect(() => { setCurrentPage((p) => Math.min(p, Math.max(1, totalPages))); }, [totalPages]);

  const sortByKey = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  const sortArrow = (key) => <span className="inline-block w-3 text-center">{sortConfig.key !== key ? '' : sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'full':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getMemberEmail = (member) => {
    if (!member) return '';
    if (typeof member === 'string') return member;
    if (member.email) return member.email;
    return String(member);
  };

  /** ===============================
   *  ACTION HANDLERS (ADD / REMOVE MEMBERS)
   * =============================== */
  const handleAddMember = async (groupId) => {
    const email = (addMemberInput[groupId] || '').trim();
    if (!email) return toast.error(t('adminGroups.enterEmail'));

    const group = groups.find((g) => g._id === groupId);
    if (!group) return toast.error(t('adminGroups.groupNotFound'));

    const existsInMembers = (group.members || []).some(
      (m) => getMemberEmail(m).toLowerCase() === email.toLowerCase()
    );
    const existsInInvited = (group.invitedEmails || []).some(
      (e) => e.toLowerCase() === email.toLowerCase()
    );

    if (existsInMembers || existsInInvited) return toast.error(t('adminGroups.alreadyAdded'));

    try {
      await api.patch(`/api/groups/${groupId}/members/add`, { email });
      // Reload instead of using the response: it is not populated (names would show as ids)
      fetchGroups();
      setAddMemberInput((prev) => ({ ...prev, [groupId]: '' }));
      toast.success(`Added/invited ${email}`);
    } catch (err) {
      console.error('Add member error', err);
      toast.error(err?.response?.data?.message || t('adminGroups.addFailed'));
      fetchGroups();
    }
  };

  const handleRemoveMemberConfirmed = async (groupId, email) => {
    try {
      await api.patch(`/api/groups/${groupId}/members/remove`, { email });
      // Reload instead of using the response: it is not populated (names would show as ids)
      fetchGroups();
      toast.success(`Removed ${email}`);
    } catch (err) {
      console.error('Remove member error', err);
      toast.error(err?.response?.data?.message || t('adminGroups.removeFailed'));
      fetchGroups();
    }
  };

  const requestRemoveMember = (groupId, email) => {
    if (!email) return toast.error(t('adminGroups.selectMember'));
    setConfirmRemove({ isOpen: true, groupId, email });
  };

  const confirmRemoveMember = () => {
    const { groupId, email } = confirmRemove;
    if (!groupId || !email) return setConfirmRemove({ isOpen: false, groupId: null, email: null });
    handleRemoveMemberConfirmed(groupId, email);
    setConfirmRemove({ isOpen: false, groupId: null, email: null });
  };

  const cancelRemoveMember = () => setConfirmRemove({ isOpen: false, groupId: null, email: null });

  const handleStatusChange = async (groupId, newStatus) => {
    try {
      const res = await api.patch(`/api/groups/${groupId}/status`, { status: newStatus });
      const updated = res.data.group || res.data;
      setGroups((prev) => prev.map((g) => (g._id === groupId ? updated : g)));
      toast.success(t('adminGroups.statusUpdated', { status: t('adminGroups.' + newStatus) }));
    } catch (err) {
      console.error('Failed to update status:', err);
      toast.error(t('adminGroups.statusFailed'));
    }
  };

  const confirmDeleteGroup = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/api/groups/${deleteTarget._id}`);
      setGroups((prev) => prev.filter((g) => g._id !== deleteTarget._id));
      toast.success(`Group ${deleteTarget.name || ''} deleted`);
    } catch (err) {
      console.error('Failed to delete group:', err);
      toast.error(t('adminGroups.deleteFailed'));
    } finally {
      setDeleteTarget(null);
    }
  };

  const openSupplierModal = (supplier, groupId = null) => {
    if (!supplier) return;
    setSelectedSupplier(supplier);
    setSelectedGroupId(groupId);
    setSupplierModalOpen(true);
  };
  const closeSupplierModal = () => {
    setSelectedSupplier(null);
    setSupplierModalOpen(false);
  };

  /** ===============================
   *  RENDER
   * =============================== */
  const renderGroup = (group) => {
          const effectiveStatus = effectiveStatusOf(group);
          // Server-computed grants for this user on this group (see groupForViewer)
          const myPermissions = group.myPermissions || [];
          const canMembers = myPermissions.includes('members');
          const canSettings = myPermissions.includes('settings');
          // Members only ever hold delegated grants; status and delete are organizer-only
          const isDelegateOnly = currentUser?.role === 'member';
          const memberOptions = [
            ...(group.members || []).map((m) => {
              const email = getMemberEmail(m);
              const idPart = typeof m === 'object' && m._id ? m._id : email;
              // Protected members cannot be removed until ownership moves or the delegation is revoked
              const memberId = typeof m === 'object' && m._id ? String(m._id) : null;
              const isOrganizer = memberId && String(group.createdBy?._id ?? group.createdBy) === memberId;
              const isActiveDelegate = memberId && (group.delegates || []).some((d) => String(d.user?._id ?? d.user) === memberId && d.permissions?.length);
              const protectedReason = isOrganizer
                ? t('adminGroups.protectedOrganizer')
                : isActiveDelegate ? t('adminGroups.protectedDelegate') : null;
              return { key: `member-${idPart}`, value: email, label: email, protectedReason };
            }),
            ...((group.invitedEmails || [])
              .filter((e) => !(group.members || []).some((m) => getMemberEmail(m).toLowerCase() === e.toLowerCase()))
              .map((e) => ({ key: `invite-${e}`, value: e, label: t('adminGroups.invitedTag', { email: e }) }))),
          ];

          const memberPanel = (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto overflow-x-hidden break-words">
                {canMembers && <>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder={t('adminGroups.searchMember')}
                      aria-label={t('adminGroups.searchMember')}
                      value={addMemberInput[group._id] || ""}
                      onChange={(e) => setAddMemberInput((prev) => ({ ...prev, [group._id]: e.target.value }))}
                      className="flex-1 min-w-0 border rounded px-3 py-2 text-sm"
                      autoComplete="off"
                    />
                    <button type="button" onClick={() => handleAddMember(group._id)} disabled={!addMemberInput[group._id]?.trim()} className="px-4 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-50">{t('adminGroups.add')}</button>
                  </div>
                  {[
                    { title: t('adminGroups.activeMembers'), items: memberOptions.filter((o) => o.key.startsWith("member-")) },
                    { title: t('adminGroups.pendingInvites'), empty: t('adminGroups.noPending'), items: memberOptions.filter((o) => o.key.startsWith("invite-")) },
                  ].map((section) => (
                    <div key={section.title}>
                      <h4 className="text-xs font-semibold uppercase text-gray-500 mt-3 mb-1">{section.title}</h4>
                      {section.empty && section.items.length === 0 && <p className="text-sm text-gray-500">{section.empty}</p>}
                      <ul className="border rounded bg-white divide-y">
                        {section.items
                          .filter((opt) => opt.label.toLowerCase().includes((addMemberInput[group._id] || "").trim().toLowerCase()))
                          .map((opt) => (
                            <li key={opt.key} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                              <span className="break-all min-w-0">{opt.label}</span>
                              {opt.key.startsWith("member-") && (
                                <div className="relative shrink-0">
                                  <button type="button" aria-label={t('adminGroups.actionsFor', { email: opt.value })} aria-haspopup="menu" aria-expanded={openMenu === opt.key} onClick={() => setOpenMenu(openMenu === opt.key ? null : opt.key)} className="px-2 py-1 rounded hover:bg-gray-100">⋮</button>
                                  {openMenu === opt.key && (
                                    <div role="menu" className="absolute right-0 z-10 mt-1 bg-white border rounded shadow">
                                      {opt.protectedReason ? (
                                        <p className="px-3 py-2 text-xs text-gray-600 max-w-[16rem]">{opt.protectedReason}</p>
                                      ) : (
                                        <button type="button" role="menuitem" onClick={() => { setOpenMenu(null); requestRemoveMember(group._id, opt.value); }} className="block w-full text-left px-3 py-2 text-red-600 hover:bg-gray-100 whitespace-nowrap">{t('adminGroups.remove')}</button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {opt.key.startsWith("invite-") && (
                                <button type="button" onClick={() => requestRemoveMember(group._id, opt.value)} className="shrink-0 px-2 py-1 rounded border text-red-600 hover:bg-gray-100 whitespace-nowrap">{t('adminGroups.cancelInvite')}</button>
                              )}
                            </li>
                          ))}
                      </ul>
                    </div>
                  ))}
                </>}

                    <GroupRolesPanel group={group} currentUser={currentUser} onChanged={fetchGroups} />
            </div>
          );

                const membersDialog = expandedGroupId === group._id && (
                  <div className="fixed inset-0 z-40 bg-black/40 flex sm:items-center sm:justify-center sm:p-4" onClick={() => setExpandedGroupId(null)}>
                    <div role="dialog" aria-modal="true" aria-label={t('adminGroups.membersOf', { name: group.name })} id={`members-${group._id}`} className="fixed left-4 right-4 top-16 bottom-4 sm:static sm:h-auto sm:w-full bg-white text-gray-800 sm:max-w-3xl sm:max-h-[85vh] sm:rounded-lg shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
                        <h3 className="font-semibold break-words min-w-0">{t('adminGroups.manageMembers')} · {group.name}</h3>
                        <button type="button" onClick={() => setExpandedGroupId(null)} aria-label="Close" className="text-2xl px-2 shrink-0">×</button>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4">
                        {memberPanel}
                      </div>
                    </div>
                  </div>
                );

    // Photo controls shared by the desktop cell and the mobile card (same props, handlers and permissions)
    const photos = (
      <SupplierImages
        supplierId={group._id}
        endpoint={`/api/groups/${group._id}/images`}
        galleryTitle={t('adminGroups.photosGroup')}
        images={group.images}
        canManage={(group.myPermissions || []).includes('settings')}
        compact
        onChange={(images) => {
          groupsRequestSeq.current += 1; // invalidate any list fetch started before this save
          setGroups((prev) => prev.map((g) => (g._id === group._id ? { ...g, images } : g)));
        }}
      />
    );

    if (isMobile) {
      const isOpen = expandedCardId === group._id;
      const supplierName = group.supplierId?.displayName || group.supplierId?.name;
      const linkClass = 'text-white px-2 py-1 rounded whitespace-nowrap text-sm';
      return (
        <li key={group._id} className="min-w-0 border rounded-md bg-white shadow-sm p-3 text-sm">
          <div className="flex items-start gap-2 min-w-0">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-gray-800 truncate" title={group.name}>{group.name}</div>
              <div className="text-gray-600 truncate" title={supplierName}>{supplierName || 'N/A'}</div>
            </div>
            <button type="button" onClick={() => setExpandedCardId(isOpen ? null : group._id)} aria-expanded={isOpen} className="shrink-0 p-1 text-blue-600 focus:outline-none">
              <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
            </button>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-600">
            <span className={`px-2 py-1 rounded font-medium whitespace-nowrap ${getStatusBadgeClass(effectiveStatus)}`}>{t('adminGroups.' + effectiveStatus)}</span>
            <span className="min-w-0 break-words">{animalLabel(t, group.animal) || t('common.na')}</span>
          </div>
          <dl className="mt-2 grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs text-gray-600">
            <dt className="font-medium text-gray-700">{t('groups.cols.portionsRemaining')}</dt>
            <dd className="min-w-0 break-words">{group.portionsRemaining ?? t('common.na')}</dd>
            <dt className="font-medium text-gray-700">{t('groups.card.pricePerFull')}</dt>
            <dd className="min-w-0 break-words">${Number(group.costPerPortion || 0).toFixed(2)}</dd>
          </dl>
          {isOpen && (
            <div className="mt-3 border-t border-gray-200 pt-2 space-y-2">
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs text-gray-600">
                <dt className="font-medium text-gray-700">{t('adminGroups.photos')}</dt>
                <dd className="min-w-0">{photos}</dd>
                <dt className="font-medium text-gray-700">{t('adminGroups.state')}</dt>
                <dd className="min-w-0 break-words">{stateLabel(group.state)}</dd>
                <dt className="font-medium text-gray-700">{t('adminGroups.visibility')}</dt>
                <dd className="min-w-0 break-words capitalize">{t('adminGroups.' + (group.visibility || 'public'), { defaultValue: group.visibility || 'public' })}</dd>
                <dt className="font-medium text-gray-700">{t('adminGroups.members')}</dt>
                <dd>{t('adminGroups.memberCount', { count: (group.members || []).length })}</dd>
                <dt className="font-medium text-gray-700">{t('adminGroups.created')}</dt>
                <dd>{group.createdAt ? <>{new Date(group.createdAt).toLocaleDateString()}<br />{new Date(group.createdAt).toLocaleTimeString()}</> : '—'}</dd>
                <dt className="font-medium text-gray-700">{t('adminGroups.updated')}</dt>
                <dd>{group.updatedAt ? <>{new Date(group.updatedAt).toLocaleDateString()}<br />{new Date(group.updatedAt).toLocaleTimeString()}</> : '—'}</dd>
              </dl>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => setExpandedGroupId((prev) => (prev === group._id ? null : group._id))} aria-expanded={expandedGroupId === group._id} aria-controls={`members-${group._id}`} className="bg-gray-100 border px-2 py-1 rounded text-sm whitespace-nowrap">{t('adminGroups.manageMembers')}</button>
                <Link to={`/admin/groups/view/${group._id}`} className={`bg-blue-500 ${linkClass}`}>{t('adminGroups.view')}</Link>
                {canSettings && <Link to={`/admin/groups/edit/${group._id}`} state={{ fromList: true }} className={`bg-yellow-500 ${linkClass}`}>{t('adminGroups.edit')}</Link>}
                {!isDelegateOnly && <button type="button" onClick={() => setDeleteTarget(group)} className="bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded whitespace-nowrap text-sm">{t('adminGroups.delete')}</button>}
              </div>
              {!isDelegateOnly && <label className="block text-xs text-gray-600">
                {t('adminGroups.stored')}{' '}
                <select value={group.status} onChange={(e) => handleStatusChange(group._id, e.target.value)} className={`border rounded px-1 py-0.5 ${getStatusBadgeClass(group.status)}`}>
                  <option value="open">{t('adminGroups.open')}</option>
                  <option value="full">{t('adminGroups.full')}</option>
                  <option value="closed">{t('adminGroups.closed')}</option>
                </select>
              </label>}
            </div>
          )}
          {membersDialog}
        </li>
      );
    }
          return (
            <React.Fragment key={group._id}>
            <tr className="border-t hover:bg-gray-50 align-top">
              <td className="p-3 break-words min-w-0"><TruncatedText text={group.name} /></td>
              <td className="p-3">
                {/* Photos: controls for users the server marks as able to manage this group (myPermissions) */}
                {photos}
              </td>
              <td className="p-3">
                {group.supplierId?.name ? (
                  <button
                    type="button"
                    onClick={() => openSupplierModal(group.supplierId, group._id)}
                    className="text-blue-600 hover:underline focus:outline-none"
                    title={t('orders.viewSupplier')}
                  >
                    <TruncatedText text={group.supplierId.displayName || group.supplierId.name} maxWidthClass="max-w-[10rem]" />
                  </button>
                ) : 'N/A'}
              </td>
              <td className="p-3">{stateLabel(group.state)}</td>
              <td className="p-3 capitalize">{t('adminGroups.' + (group.visibility || 'public'), { defaultValue: group.visibility || 'public' })}</td>
              <td className="p-3">
                <div className="flex flex-col gap-1">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(effectiveStatus)}`}>
                    {t('adminGroups.effectiveLabel')}: {t('adminGroups.' + effectiveStatus)}
                  </span>
                  {!isDelegateOnly && <label className="text-xs text-gray-600">
                    {t('adminGroups.stored')}{' '}
                    <select
                      value={group.status}
                      onChange={(e) => handleStatusChange(group._id, e.target.value)}
                      className={`border rounded px-1 py-0.5 ${getStatusBadgeClass(group.status)}`}
                    >
                      <option value="open">{t('adminGroups.open')}</option>
                      <option value="full">{t('adminGroups.full')}</option>
                      <option value="closed">{t('adminGroups.closed')}</option>
                    </select>
                  </label>}
                </div>
              </td>

              {/* Members */}
              <td className="p-3 min-w-[10rem]">
                {/* Compact summary; the controls below open on demand */}
                <button
                  type="button"
                  onClick={() => setExpandedGroupId((prev) => (prev === group._id ? null : group._id))}
                  aria-expanded={expandedGroupId === group._id}
                  aria-controls={`members-${group._id}`}
                  className="w-full flex items-center justify-between gap-2 text-sm border rounded-md px-3 py-2 min-h-11 sm:min-h-0 bg-white hover:bg-gray-50"
                >
                  <span>{t('adminGroups.memberCount', { count: (group.members || []).length })}</span>
                  <span className="text-blue-600">{expandedGroupId === group._id ? t('adminGroups.hide') + ' ▲' : t('adminGroups.manage') + ' ▼'}</span>
                </button>
                {membersDialog}
              </td>
              <td className="p-3 text-sm">{group.createdAt ? <>{new Date(group.createdAt).toLocaleDateString()}<br />{new Date(group.createdAt).toLocaleTimeString()}</> : '—'}</td>
              <td className="p-3 text-sm">{group.updatedAt ? <>{new Date(group.updatedAt).toLocaleDateString()}<br />{new Date(group.updatedAt).toLocaleTimeString()}</> : '—'}</td>

              {/* Actions */}
              <td className="p-3">
                <div className="flex flex-wrap gap-2">
                <Link
                  to={`/admin/groups/view/${group._id}`}
                  className="bg-blue-500 text-white px-2 py-1 rounded whitespace-nowrap"
                >
                  {t('adminGroups.view')}
                </Link>
                {canSettings && <Link
                  to={`/admin/groups/edit/${group._id}`}
                  state={{ fromList: true }}
                  className="bg-yellow-500 text-white px-2 py-1 rounded whitespace-nowrap"
                >
                  {t('adminGroups.edit')}
                </Link>}
                {!isDelegateOnly && <button
                  type="button"
                  onClick={() => setDeleteTarget(group)}
                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 whitespace-nowrap"
                >
                  {t('adminGroups.delete')}
                </button>}
                </div>
              </td>
            </tr>
            </React.Fragment>
          );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <h1 className="text-2xl font-semibold text-gray-800">{t('adminGroups.title')}</h1>
          {currentUser?.role !== 'member' && (
            <Link
              to="/admin/create-group"
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              + {t('nav.createGroup')}
            </Link>
          )}
        </div>

        {reviewGroupId && (
          <JoinRequestsPanel groupId={reviewGroupId} highlightRequestId={reviewRequestId} />
        )}

        {/* Filters + Search */}
        <div className="mb-4 flex gap-4 flex-wrap items-center">
          <div>
            <label className="mr-2 text-gray-700">{t('adminGroups.filterStatus')}</label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="border px-3 py-1 rounded"
            >
              <option value="all">{t('adminGroups.statusAll')}</option>
              <option value="open">{t('adminGroups.open')}</option>
              <option value="full">{t('adminGroups.full')}</option>
              <option value="closed">{t('adminGroups.closed')}</option>
              <option value="effective">{t('adminGroups.effective')}</option>
            </select>
          </div>
          <input
            type="text"
            placeholder={t('adminGroups.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="border px-3 py-1 rounded w-full sm:w-80"
          />
          <div className="text-sm italic self-center">
            <em>{t('adminGroups.effectiveNote')}</em>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <p className="text-gray-600">{t('adminGroups.loading')}</p>
        ) : filtered.length === 0 ? (
          <>
            {!loadError && <p className="mb-2 text-sm text-gray-600">{t('adminGroups.showing', { count: 0, total: 0 })}</p>}
            <p className="text-gray-600">{translateKnown(t, loadError) || t('adminGroups.noGroups')}</p>
          </>
        ) : (
          <>
            <p className="mb-2 text-sm text-gray-600">
              {t('adminGroups.showingRange', { start: indexOfFirst + 1, end: Math.min(indexOfLast, sortedFiltered.length), total: sortedFiltered.length })}
            </p>
                {isMobile ? (
                <ul className="space-y-3">{currentGroups.map(renderGroup)}</ul>
) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[72rem] bg-white border rounded shadow">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3 text-left w-56 cursor-pointer" onClick={() => sortByKey('name')}>{t('adminGroups.name')}{sortArrow('name')}</th>
                  <th className="p-3 text-left">{t('adminGroups.photos')}</th>
                  <th className="p-3 text-left w-56 cursor-pointer" onClick={() => sortByKey('supplierId')}>{t('adminGroups.supplier')}{sortArrow('supplierId')}</th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('state')}>{t('adminGroups.state')}{sortArrow('state')}</th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('visibility')}>{t('adminGroups.visibility')}{sortArrow('visibility')}</th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('effectiveStatus')}>{t('adminGroups.status')}{sortArrow('effectiveStatus')}</th>
                  <th className="p-3 text-left w-96">{t('adminGroups.members')}</th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('createdAt')}>{t('adminGroups.created')}{sortArrow('createdAt')}</th>
                  <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('updatedAt')}>{t('adminGroups.updated')}{sortArrow('updatedAt')}</th>
                  <th className="p-3 text-left">{t('adminGroups.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {currentGroups.map(renderGroup)}
              </tbody>
            </table>
            </div>
            )}

            <Pagination page={currentPage} pageCount={totalPages} onChange={setCurrentPage} />
          </>
        )}

      </div>

      {/* Modals */}
      <ConfirmationModal
        isOpen={confirmRemove.isOpen}
        message={t('adminGroups.removeConfirm', { email: confirmRemove.email })}
        onConfirm={confirmRemoveMember}
        onClose={cancelRemoveMember}
      />

      <SupplierInfoModal
        isOpen={supplierModalOpen}
        supplier={selectedSupplier}
        groupId={selectedGroupId}
        onClose={closeSupplierModal}
      />

      <ConfirmationModal
        isOpen={!!deleteTarget}
        message={t('adminGroups.deleteConfirm', { name: deleteTarget?.name })}
        onConfirm={confirmDeleteGroup}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
